/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { createGroupIndexDriver, createTableDriver, Table, whenExisting } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  arrayIterator,
  booleanComparator,
  composeComparators,
  valueComparator,
} from '@gh/core-util';

import { LeadBoardInfo, LeadBoardListItem, LeadBoardState } from '../../resources/lead-board.model';

export const PRIORITY_LEAD_TABLE_GROUP = 'priority';
export const STAGE_LEAD_TABLE_GROUP = 'stage';

export const getLeadBoardItemId = ({ salesFolderId }: LeadBoardListItem) => salesFolderId;

export const leadBoardItemComparator = composeComparators<LeadBoardListItem>(
  booleanComparator('isNewWebsiteLead', 'desc'),
  valueComparator('followUpDate'),
  valueComparator('contactAddressee'),
  valueComparator(getLeadBoardItemId),
);

export const leadBoardTable = createTableDriver<LeadBoardListItem>(
  getLeadBoardItemId,
  createGroupIndexDriver(
    PRIORITY_LEAD_TABLE_GROUP,
    getLeadBoardItemId,
    whenExisting,
    ({ priority }: LeadBoardListItem) => priority,
    leadBoardItemComparator,
  ),
  createGroupIndexDriver(
    STAGE_LEAD_TABLE_GROUP,
    getLeadBoardItemId,
    whenExisting,
    ({ priority, stage }) => getLeadBoardColumnId(priority, stage),
    leadBoardItemComparator,
  ),
);

export const queryLeadBoardPanelColumnList = ({ boardTable }: LeadBoardState, priority: number, stage: number) =>
  leadBoardTable.byGroup(boardTable, STAGE_LEAD_TABLE_GROUP, getLeadBoardColumnId(priority, stage));

export const queryIsBoardEmpty = ({ boardTable }: LeadBoardState) => leadBoardTable.isEmpty(boardTable);

export function initLeadBoardInfo(salesPersonId: Maybe<ID>, filter: any): LeadBoardInfo {
  return {
    salesPersonId: salesPersonId || filter.salesPersonId,
    showCompleted: !!filter.showCompleted,
    showActive: !!filter.showActive,
    showInactive: !!filter.showInactive,
    showSuppressed: false,
    showOverdue: !!filter.showOverdue,
    searchString: void 0,
  };
}

export function getLeadBoardColumnId(priority: number, stage: number): string {
  return `${priority}:${stage}`;
}

export function buildLeadBoardTable(list: LeadBoardListItem[]): Table<LeadBoardListItem> {
  return leadBoardTable.create(arrayIterator(list));
}
